export default function HeroSection() {
  const handleClick = (href: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative w-full min-h-[580px] md:min-h-[680px] flex items-center overflow-hidden pt-16"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20cinematic%20abstract%20background%20with%20soft%20golden%20ambient%20lighting%20and%20subtle%20film%20reel%20elements%20elegant%20composition%20with%20dark%20warm%20tones%20and%20gentle%20glow%20effects%20artistic%20movie%20theater%20atmosphere%20with%20bokeh%20light%20particles%20sophisticated%20cinematic%20aesthetic&width=1600&height=900&seq=hero-cinema-2026-01&orientation=landscape"
          alt="春夜影院电影背景"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/55"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-4 md:px-6">
        <div className="max-w-[1440px] mx-auto flex flex-col lg:flex-row items-center gap-8 md:gap-12 lg:gap-16">
          {/* Left text column */}
          <div className="w-full lg:w-[55%] flex flex-col items-start text-left">
            {/* Tag */}
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500/20 border border-primary-400/30 text-primary-300 text-xs md:text-sm font-medium mb-4 md:mb-5 whitespace-nowrap">
              <i className="ri-sparkling-2-line text-sm"></i>
              蓝光画质 · 流畅播放 · 免费观影
            </span>

            {/* H1 */}
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              春夜影院 - 免费高清电影电视剧在线观看
            </h1>

            {/* Slogan */}
            <p className="text-base md:text-lg text-white/80 font-heading mb-5 md:mb-6">
              春夜时刻，畅享光影世界
            </p>

            {/* Description */}
            <p className="text-sm md:text-base text-white/65 leading-relaxed mb-6 md:mb-7 max-w-xl">
              春夜影院是专业的免费高清影视在线观看平台，覆盖最新电影、热播电视剧、热门综艺、动漫番剧等丰富内容。每日更新海量资源，蓝光画质清晰细腻，多线路智能切换确保不缓冲流畅播放。无论电脑端还是手机版，随时随地畅享极致观影体验。
            </p>

            {/* Stats */}
            <div className="flex flex-wrap items-center gap-5 md:gap-8 mb-7 md:mb-8">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 md:w-11 md:h-11 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center">
                  <i className="ri-refresh-line text-lg md:text-xl text-primary-400"></i>
                </div>
                <div>
                  <div className="text-base md:text-lg font-bold text-white whitespace-nowrap">每日更新</div>
                  <div className="text-xs text-white/50 whitespace-nowrap">海量资源实时同步</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 md:w-11 md:h-11 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center">
                  <i className="ri-film-line text-lg md:text-xl text-primary-400"></i>
                </div>
                <div>
                  <div className="text-base md:text-lg font-bold text-white whitespace-nowrap">万部资源</div>
                  <div className="text-xs text-white/50 whitespace-nowrap">电影电视剧全覆盖</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 md:w-11 md:h-11 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center">
                  <i className="ri-device-line text-lg md:text-xl text-primary-400"></i>
                </div>
                <div>
                  <div className="text-base md:text-lg font-bold text-white whitespace-nowrap">全端支持</div>
                  <div className="text-xs text-white/50 whitespace-nowrap">电脑手机平板通用</div>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <a
                href="#carousel"
                onClick={handleClick('#carousel')}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm md:text-base transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-circle-line text-lg"></i>
                开始观影
              </a>
              <a
                href="#ranking"
                onClick={handleClick('#ranking')}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white font-medium text-sm md:text-base transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-fire-line text-lg"></i>
                查看热播榜单
              </a>
            </div>
          </div>

          {/* Right visual column */}
          <div className="w-full lg:w-[45%] flex items-center justify-center">
            <div className="relative w-full max-w-md lg:max-w-none">
              <div className="aspect-[4/3] rounded-xl overflow-hidden border border-white/10 shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Golden%20hour%20cinematic%20scene%20with%20warm%20theater%20curtains%20and%20film%20projector%20beam%20of%20light%20cutting%20through%20gentle%20atmosphere%20elegant%20vintage%20cinema%20aesthetic%20with%20rich%20dark%20amber%20tones%20soft%20dust%20particles%20floating%20in%20projector%20light%20beam&width=900&height=675&seq=hero-visual-2026-01&orientation=landscape"
                  alt="春夜影院视觉主图"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Decorative floating card */}
              <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 bg-background-50/95 backdrop-blur-md rounded-lg px-4 py-3 border border-background-200/60 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                  <i className="ri-star-fill text-accent-500 text-sm"></i>
                </div>
                <div>
                  <div className="text-xs text-foreground-700 font-medium whitespace-nowrap">高清蓝光画质</div>
                  <div className="text-[10px] text-foreground-500 whitespace-nowrap">流畅不缓冲播放</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}